import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { toasterMsg } from '../../../../../Components/Toaster/Toaster';
import Loader from '../../../../../Components/Utility/Loader/Loader';
import CustomTooltip from '../../../../../Components/Tooltip/CustomTooltip';
import DragDropSort from '../../../../../Components/DragDropSort/DragDropSort';
import CustomConfirmationPopup from '../../../../../Components/ModalPopup/CustomConfirmationPopup';
import { POCreationProvider, usePOCreation } from '../../../../DevlopmentOrder/ProductPurchaseOrder/contextProvider/POCreationContext';
import PODetailsForPO from '../../../../DevlopmentOrder/ProductPurchaseOrder/Components/PODetailsForPO';
import CompanyAndSupplierForPO from '../../../../DevlopmentOrder/ProductPurchaseOrder/Components/CompanyAndSupplierForPO';
import ShippingDetailsForPO from '../../../../DevlopmentOrder/ProductPurchaseOrder/Components/ShippingDetailsForPO';
import GrandTotalForPO from '../../../../DevlopmentOrder/ProductPurchaseOrder/Components/GrandTotalForPO';
import OrderDetailsForPO from '../../../../DevlopmentOrder/ProductPurchaseOrder/Components/OrderDetailsForPO';
import SectionForPO from '../../../../DevlopmentOrder/ProductPurchaseOrder/Components/SectionForPO';
import {
  fetchSupplierFactories,
  clearSupplierFactories,
  fetchCompanyDetailsForPO,
  fetchFilteredDevelopmentOrders,
  clearFilteredDevelopmentOrders,
} from '../../../../DevlopmentOrder/ProductPurchaseOrder/Store/productPurchaseOrder.action';
import types from '../../../../DevlopmentOrder/ProductPurchaseOrder/Store/productPurchaseOrder.types';
import { getSuppliers } from '../../CostingTsx/store/Costing.action';
import { getBrandDetails } from '../../../../SystemMasters/Store/SystemMasters.action';
import '../../../../DevlopmentOrder/ProductPurchaseOrder/CreateEditPO/CreateEditPO.css';

function CreatePOInternal() {
  const { t } = useTranslation('purchase-order');
  const history = useHistory();
  const dispatch = useDispatch();

  const [showRearrangeModal, setShowRearrangeModal] = useState(false);
  const [companySuppliers, setCompanySuppliers] = useState([]);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);

  const {
    creationState,
    handlePOFieldChange,
    setValidationError,
    fetchTemplateList,
    fetchTemplateDetails,
    clearTemplateDetails,
    clearAllPOData,
    updateSectionsOrder,
    savePurchaseOrder,
    isFieldsDisabledByStatus,
  } = usePOCreation();

  const {
    UserDetails: { userInfo, isSupplierLoggedIn },
    purchaseOrderTemplateReducer: { templateList } = {},
    productPurchaseOrderReducer: { supplierFactories, poProductDetails, companyDetails },
    SystemMastersDetails: { brandList = [] } = {},
  } = useSelector((reducer) => reducer);

  useEffect(() => {
    const fetchCompanySuppliers = async () => {
      setIsLoadingSuppliers(true);
      try {
        const suppliers = await dispatch(
          getSuppliers({
            loggedInUserId: userInfo?.userId,
          }),
        );

        if (suppliers && Array.isArray(suppliers)) {
          const transformedSuppliers = suppliers.map((supplier) => ({
            _id: supplier._id,
            name: supplier.name,
            label: supplier.name,
            value: supplier._id,
            factories: supplier.factories || [],
          }));
          setCompanySuppliers(transformedSuppliers);
        }
      } catch (error) {
        console.error('Error in fetchCompanySuppliers:', error);
      } finally {
        setIsLoadingSuppliers(false);
      }
    };

    if (userInfo?.userId) {
      fetchCompanySuppliers();
    }
  }, [dispatch, userInfo?.userId]);

  useEffect(() => {
    fetchTemplateList();
    dispatch(fetchCompanyDetailsForPO());
  }, [fetchTemplateList, dispatch]);

  useEffect(() => {
    if (userInfo?.userId) {
      dispatch(getBrandDetails({ isActive: true, loggedInUserId: userInfo.userId }));
    }
  }, [dispatch, userInfo?.userId]);

  useEffect(() => {
    if (!userInfo) return;

    if (!creationState.templateSelected) return;

    if (poProductDetails && poProductDetails.companyDetails) {
      return;
    }

    const enhancedSuppliers = creationState.selectedSupplier
      ? [
          {
            _id: creationState.selectedSupplier._id,
            name: creationState.selectedSupplier.name,
            email: creationState.selectedSupplier.email || '',
            companies: [
              {
                companyName: userInfo.companyName,
                supplierName: creationState.selectedSupplier.name || creationState.selectedSupplier.label,
                code: creationState.selectedSupplier.code || '',
                internalPointContact: {
                  firstName: creationState.selectedSupplier.contactFirstName || '',
                  lastName: creationState.selectedSupplier.contactLastName || '',
                  email: creationState.selectedSupplier.email || '',
                },
                streetAddress: creationState.selectedSupplier.address || creationState.selectedSupplier.streetAddress || '',
              },
            ],
          },
        ]
      : [];

    const enhancedFactories = creationState.selectedFactory
      ? [
          {
            _id: creationState.selectedFactory.factoryId,
            factoryId: creationState.selectedFactory.factoryId,
            factoryName: creationState.selectedFactory.factoryName,
            name: creationState.selectedFactory.factoryName,
            factoryCode: creationState.selectedFactory.factoryCode,
            code: creationState.selectedFactory.factoryCode,
            supplierId: creationState.selectedSupplier?._id,
            companyName: userInfo.companyName,
            buildingNo: creationState.selectedFactory.buildingNo || '',
            townCity: creationState.selectedFactory.townCity || creationState.selectedFactory.city || '',
            country: creationState.selectedFactory.country || '',
            zipCode: creationState.selectedFactory.zipCode || '',
            streetAddress: creationState.selectedFactory.streetAddress || creationState.selectedFactory.address || '',
            portOfLanding: creationState.selectedFactory.portOfLanding || creationState.selectedFactory.portOfLoading || '',
            paymentTerms: creationState.selectedFactory.paymentTerms || '',
            contactPersons: creationState.selectedFactory.contactPersons || [],
            internalPointContact: {
              firstName: creationState.selectedFactory.contactFirstName || '',
              lastName: creationState.selectedFactory.contactLastName || '',
              email: creationState.selectedFactory.email || creationState.selectedFactory.contactEmail || '',
            },
          },
        ]
      : [];

    const minimalProductDetails = {
      companyDetails: companyDetails || {
        companyName: userInfo.companyName || '',
        companyRegistration: userInfo.companyRegistration || '',
        vatRegistrationNumber: userInfo.vatRegistrationNumber || '',
        streetAddress: userInfo.streetAddress || '',
        buildingNumber: userInfo.buildingNumber || '',
        city: userInfo.city || '',
        zipCode: userInfo.zipCode || '',
        country: userInfo.country || '',
        distributionCentres: userInfo.distributionCentres || [],
        contactPerson: userInfo.contactPerson || '',
        contactPersonEmail: userInfo.contactPersonEmail || '',
      },
      suppliers: enhancedSuppliers,
      supplierFactories: enhancedFactories,
    };

    dispatch({
      type: types.FETCH_PRODUCT_DETAILS,
      payload: minimalProductDetails,
    });
  }, [userInfo, creationState.selectedSupplier, creationState.selectedFactory, creationState.templateSelected, poProductDetails, companyDetails, dispatch]);

  const supplierOptions = useMemo(() => {
    return companySuppliers;
  }, [companySuppliers]);

  const factoryOptions = useMemo(() => {
    if (!creationState.selectedSupplier) return [];

    if (supplierFactories?.length > 0) {
      return supplierFactories.map((factory) => ({
        ...factory,
        _id: factory.factoryId,
        name: factory.factoryName,
        label: factory.factoryName,
        value: factory.factoryIds,
      }));
    }

    return [];
  }, [creationState.selectedSupplier, supplierFactories]);

  const templateOptions = useMemo(() => {
    if (!templateList || templateList.length === 0) return [];

    return templateList.map((template) => ({
      _id: template._id,
      label: template.templateName,
      value: template._id,
      ...template,
    }));
  }, [templateList]);

  const brandOptions = useMemo(() => {
    const list = Array.isArray(brandList) ? brandList.filter((b) => b.isActive !== false) : [];
    return list.map((b) => ({
      _id: b._id,
      name: b.name,
      label: b.name,
      value: b._id,
    }));
  }, [brandList]);

  const handleBack = useCallback(() => {
    clearAllPOData();
    dispatch(clearSupplierFactories());
    history.push('/todo-list/product-purchase-order-todo');
  }, [clearAllPOData, dispatch, history]);

  const handleSave = useCallback(async () => {
    const result = await savePurchaseOrder();
    if (result?.success) {
      toasterMsg(t('CREATE_EDIT_PO.CREATED_SUCCESS'), 'success');
      setTimeout(() => {
        handleBack();
      }, 1000);
    } else {
      console.error('Error in handleSave:', result);
    }
  }, [savePurchaseOrder, handleBack]);

  const handleOnChange = useCallback(
    (value, field) => {
      handlePOFieldChange(field, value);

      if (field === 'selectedSupplier') {
        handlePOFieldChange('selectedFactory', null);
        handlePOFieldChange('selectedTemplate', null);
        clearTemplateDetails();
        dispatch(clearSupplierFactories());
        setValidationError('selectedFactory', null);
        setValidationError('selectedTemplate', null);

        if (value && value._id) {
          dispatch(fetchSupplierFactories(value._id));
          dispatch(fetchFilteredDevelopmentOrders(value._id, null, creationState.selectedBrand?._id ?? creationState.selectedBrand));
        } else {
          dispatch(clearFilteredDevelopmentOrders());
        }
      }

      if (field === 'selectedFactory') {
        handlePOFieldChange('selectedTemplate', null);
        clearTemplateDetails();
        setValidationError('selectedTemplate', null);

        if (creationState.selectedSupplier?._id) {
          dispatch(
            fetchFilteredDevelopmentOrders(
              creationState.selectedSupplier._id,
              value?._id || null,
              creationState.selectedBrand?._id ?? creationState.selectedBrand,
            ),
          );
        }
      }

      if (field === 'selectedTemplate') {
        if (creationState.selectedTemplate && value && creationState.selectedTemplate._id !== value._id) {
          clearTemplateDetails();
        }

        if (value) {
          fetchTemplateDetails(value._id);
        } else {
          clearTemplateDetails();
        }
      }

      if (field === 'selectedBrand') {
        if (creationState.selectedSupplier?._id) {
          dispatch(fetchFilteredDevelopmentOrders(creationState.selectedSupplier._id, creationState.selectedFactory?._id || null, value?._id ?? value ?? null));
        }
      }
    },
    [
      handlePOFieldChange,
      clearTemplateDetails,
      dispatch,
      setValidationError,
      fetchTemplateDetails,
      creationState.selectedTemplate,
      creationState.selectedSupplier,
      creationState.selectedFactory,
      creationState.selectedBrand,
    ],
  );

  useEffect(() => {
    if (creationState.templateSelected && creationState.sections?.length > 0) {
      const hasOrderDetailsSection = creationState.sections.some(
        (section) => section.name === 'Order Details' || section.sectionKey === 'order_details_section',
      );

      if (hasOrderDetailsSection && (!creationState.orderDetails || creationState.orderDetails.length === 0)) {
        const uniqueId = `parent-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const uniqueParentRowKey = `prk-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        const emptyRow = {
          id: uniqueId,
          parentRowKey: uniqueParentRowKey,
          isSubRow: false,
          parentRowId: null,
          packType: '',
          productNo: null,
          colourWay: null,
          size: null,
          length: null,
          ratio: 0,
          packs: 0,
          quantity: 0,
          location: '',
          unitPrice: 0,
          discount: 0,
          priceAfterDiscount: 0,
          total: 0,
          approvedByDate: null,
          exFactoryDate: null,
          shipmentDate: null,
        };

        handlePOFieldChange('orderDetails', [emptyRow]);
      }
    }
  }, [creationState.templateSelected, creationState.sections, creationState.orderDetails, handlePOFieldChange]);

  useEffect(() => {
    if (supplierOptions?.length === 1 && !creationState.selectedSupplier && !isSupplierLoggedIn) {
      const singleSupplier = supplierOptions[0];
      handleOnChange(singleSupplier, 'selectedSupplier');
    }
  }, [supplierOptions, creationState.selectedSupplier, isSupplierLoggedIn, handleOnChange]);

  useEffect(() => {
    if (templateOptions?.length === 1 && !creationState.selectedTemplate && creationState.selectedSupplier) {
      const singleTemplate = templateOptions[0];
      handleOnChange(singleTemplate, 'selectedTemplate');
    }
  }, [templateOptions, creationState.selectedTemplate, creationState.selectedSupplier, handleOnChange]);

  useEffect(() => {
    if (brandOptions.length === 1 && !creationState.selectedBrand) {
      handleOnChange(brandOptions[0], 'selectedBrand');
    }
  }, [brandOptions, creationState.selectedBrand, handleOnChange]);

  const handleDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;

      const newOrder = Array.from(creationState.sections);
      const [removed] = newOrder.splice(result.source.index, 1);
      newOrder.splice(result.destination.index, 0, removed);

      updateSectionsOrder(newOrder);
    },
    [creationState.sections, updateSectionsOrder],
  );

  const toggleRearrangeModal = useCallback(() => {
    setShowRearrangeModal((prev) => !prev);
  }, []);

  const SECTION_KEY_PO_DETAILS = 'po_details_section';
  const SECTION_KEY_COMPANY_SUPPLIER = 'company_supplier_section';
  const SECTION_KEY_SHIPPING = 'shipping_details_section';
  const SECTION_KEY_GRAND_TOTAL = 'grand_total_section';
  const SECTION_KEY_ORDER_DETAILS = 'order_details_section';
  const REARRANGE_SECTIONS_KEY = 'COMMON_LABEL.REARRANGE';

  const renderSectionsInOrder = useMemo(() => {
    const sections = creationState.sections || [];
    const renderedSections = [];
    renderedSections.push(<PODetailsForPO key="po-details" />);
    renderedSections.push(<CompanyAndSupplierForPO key="company-supplier" />);

    const sortedSections = [...sections].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

    sortedSections.forEach((section) => {
      switch (section.sectionKey) {
        case SECTION_KEY_SHIPPING:
          renderedSections.push(<ShippingDetailsForPO key="shipping-details" />);
          break;
        case SECTION_KEY_GRAND_TOTAL:
          renderedSections.push(<GrandTotalForPO key="grand-total" />);
          break;
        case SECTION_KEY_ORDER_DETAILS:
          renderedSections.push(<OrderDetailsForPO key="order-details" />);
          break;
        case SECTION_KEY_PO_DETAILS:
        case SECTION_KEY_COMPANY_SUPPLIER:
          break;
        default:
          if (!section.sectionKey || section.name) {
            renderedSections.push(<SectionForPO key={`custom-${section.id}`} section={section} />);
          }
          break;
      }
    });
    return renderedSections;
  }, [creationState.sections, SECTION_KEY_PO_DETAILS, SECTION_KEY_COMPANY_SUPPLIER, SECTION_KEY_SHIPPING, SECTION_KEY_GRAND_TOTAL, SECTION_KEY_ORDER_DETAILS]);

  if (isLoadingSuppliers) {
    return <Loader />;
  }

  return (
    <>
      <div className="page-title-filter sticky">
        <div className="title-container max-w-260">
          <h4 className="title d-inline-flex bom-title-list ml-16 w-xs-200px">
            {t('CREATE_EDIT_PO.PAGE_TITLE')} | {t('CREATE_EDIT_PO.CREATE_PO')}
          </h4>
        </div>
        <div className="ml-auto search-filter">
          <div className="action-icons-container">
            <button type="button" className="btn" onClick={handleBack} aria-label={t('CREATE_EDIT_PO.BACK')}>
              <i className="icon-close f-20" />
            </button>
            <div className="floting-actions">
              <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.SAVE')}>
                <button type="button" className="btn pt-0" onClick={handleSave} disabled={creationState.isLoading} aria-label={t('COMMON_LABEL.SAVE')}>
                  <i className="icon-Save-1 f-32" />
                </button>
              </CustomTooltip>
              {creationState.templateSelected && (
                <CustomTooltip placement="top" tooltipText={t(REARRANGE_SECTIONS_KEY)}>
                  <button type="button" className="btn pt-0" onClick={toggleRearrangeModal} aria-label={t(REARRANGE_SECTIONS_KEY)}>
                    <i className="icon-Rearrange" />
                  </button>
                </CustomTooltip>
              )}
            </div>
          </div>
        </div>
      </div>
      <Container fluid className="mt-24">
        <Row className="mb-3">
          <Col md={3}>
            <div className="common-react-select all-border mb-0">
              <Select
                options={brandOptions}
                getOptionLabel={(option) => option?.name || option?.label || String(option?.value ?? '')}
                getOptionValue={(option) => option?._id || option?.id}
                className="common-select"
                classNamePrefix="common-select"
                menuPosition="fixed"
                placeholder={t('CREATE_EDIT_PO.SELECT_BRAND')}
                value={creationState.selectedBrand}
                onChange={(value) => handleOnChange(value, 'selectedBrand')}
                isDisabled={isFieldsDisabledByStatus()}
                isClearable
              />
            </div>
          </Col>
          <Col md={3}>
            <div className="common-react-select all-border mb-0">
              <Select
                options={supplierOptions}
                getOptionLabel={(option) => option?.name || option?.label || String(option?.value ?? '')}
                getOptionValue={(option) => option?._id || option?.id}
                className="common-select"
                classNamePrefix="common-select"
                menuPosition="fixed"
                placeholder={t('CREATE_EDIT_PO.SELECT_SUPPLIER')}
                value={creationState.selectedSupplier}
                onChange={(value) => handleOnChange(value, 'selectedSupplier')}
                isDisabled={isSupplierLoggedIn || isFieldsDisabledByStatus()}
                isClearable
              />
              {creationState.validationErrors?.selectedSupplier && <small className="error-message">{creationState.validationErrors.selectedSupplier}</small>}
            </div>
          </Col>
          <Col md={3}>
            <div className="common-react-select all-border mb-0">
              <Select
                options={factoryOptions}
                getOptionLabel={(option) => option?.factoryName || option?.label}
                getOptionValue={(option) => option?._id || option?.id}
                className="common-select"
                classNamePrefix="common-select"
                menuPosition="fixed"
                placeholder={t('CREATE_EDIT_PO.SELECT_FACTORY')}
                value={creationState.selectedFactory}
                onChange={(value) => handleOnChange(value, 'selectedFactory')}
                isDisabled={!creationState.selectedSupplier || isFieldsDisabledByStatus()}
                isClearable
              />
            </div>
          </Col>
          <Col md={3}>
            <div className="common-react-select all-border mb-0">
              <Select
                options={templateOptions}
                getOptionLabel={(option) => option?.label || option?.templateName || String(option?.value ?? '')}
                getOptionValue={(option) => option?._id || option?.id}
                className="common-select"
                classNamePrefix="common-select"
                menuPosition="fixed"
                placeholder={t('CREATE_EDIT_PO.SELECT_TEMPLATE')}
                value={creationState.selectedTemplate}
                onChange={(value) => handleOnChange(value, 'selectedTemplate')}
                isDisabled={!creationState.selectedSupplier || isFieldsDisabledByStatus()}
                isClearable
              />
              {creationState.validationErrors?.selectedTemplate && <small className="error-message">{creationState.validationErrors.selectedTemplate}</small>}
            </div>
          </Col>
        </Row>

        {creationState.isLoading && <Loader />}
        {!creationState.isLoading && creationState.templateSelected && <div className="po-wrapper mb-100px">{renderSectionsInOrder}</div>}
        {!creationState.isLoading && !creationState.templateSelected && (
          <div className="text-center py-5">
            <p className="text-muted">{t('CREATE_EDIT_PO.PLEASE_SELECT_TEMPLATE')}</p>
          </div>
        )}
      </Container>

      {showRearrangeModal && (
        <CustomConfirmationPopup show={showRearrangeModal} handleModal={toggleRearrangeModal} title={t(REARRANGE_SECTIONS_KEY)} hideFooter>
          <DragDropSort items={creationState.sections} onDragEnd={handleDragEnd} renderItem={(section) => <div className="p-2">{section.name}</div>} />
          <div className="text-right mt-3">
            <button type="button" className="btn btn-primary" onClick={toggleRearrangeModal}>
              {t('COMMON_LABEL.DONE')}
            </button>
          </div>
        </CustomConfirmationPopup>
      )}
    </>
  );
}

function CreatePO() {
  return (
    <POCreationProvider>
      <CreatePOInternal />
    </POCreationProvider>
  );
}

export default CreatePO;
